import { Button } from '@shared/components/common/Button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@shared/components/common/Form';
import Heading from '@shared/components/common/Heading';
import { PasswordInput } from '@shared/components/common/Input';
import { useStore } from '@shared/hooks/useStore';
import yup from '@shared/services/yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { observer } from 'mobx-react-lite';
import { flowResult } from 'mobx';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { PASSWORD_REGEX } from '@/shared/configs/regex';

export default observer(function ChangePasswordPage() {
    // store
    const {
        modalStore: { hideModal, showAlertModal },
        authStore: { changePassword, logout }
    } = useStore();

    // validate schema
    const validateSchema = yup.object().shape({
        currentPassword: yup.string().required('必須項目をご入力ください。'),
        newPassword: yup.string().required('必須項目をご入力ください。').matches(
            new RegExp(PASSWORD_REGEX),
            'アルファベットの小文字・大文字・数字を含む8～20文字でご入力ください'
        ),
        confirmPassword: yup.string()
            .oneOf([yup.ref('newPassword')], '上記パスワードと一致しないため再入力をお願いします。')
            .required('必須項目をご入力ください。')
    });

    type FormData = yup.InferType<typeof validateSchema>;

    // state
    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: {
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
        }
    });

    const [showCurrentPassword, setShowCurrentPassword] = useState<boolean>(false);
    const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

    // function
    const onSubmit = async (data: FormData) => {
        try {
            const res = await flowResult(changePassword({
                currentPassword: data.currentPassword,
                newPassword: data.newPassword,
                confirmPassword: data.confirmPassword
            }));

            if (res) {
                hideModal('form-change-password');
                showAlertModal({
                    id: 'alert-modal-change-password',
                    content: 'パスワードを変更しました。再度ログインしてください。',
                    type: 'success',
                    footerClassName: 'sm:justify-center',
                    cancelButtonClassName: 'hidden',
                    onSave: () => {
                        // hideModal('alert-modal-change-password');
                        logout();
                    },
                    showCloseIcon: false,
                    onCancel: () => {
                        // hideModal('alert-modal-change-password');
                        logout();
                    }
                });
            }
        } catch (error) {
            console.error('Failed to change password:', error);
        }
    };

    const onError = (error: any) => {
        console.error('Form validation errors:', error);
    };

    return (
        <div className='flex overflow-hidden justify-center items-center bg-white rounded-lg sm:bg-background-2'>
            <div className='flex flex-col justify-center mx-auto w-full'>
                {/* Modal Header */}
                <div className='flex items-center justify-center min-h-[100px] bg-[#E6F9EE]'>
                    <Heading className='text-center'>パスワード変更</Heading>
                </div>

                {/* Modal Content */}
                <div className='p-4 md:py-6 md:px-8'>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit, onError)} className='space-y-6 md:space-y-8'>
                            <div className='space-y-4 sm:space-y-6'>
                                <FormField
                                    control={form.control}
                                    name='currentPassword'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className='text-base' required>現在のパスワード</FormLabel>
                                            <FormControl>
                                                <PasswordInput
                                                    type={!showCurrentPassword ? 'password' : 'text'}
                                                    placeholder='現在のパスワード'
                                                    onToggleIcon={() => setShowCurrentPassword(!showCurrentPassword)}
                                                    {...field}
                                                    className='h-14 rounded-none'
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name='newPassword'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className='text-base' required>新しいパスワード</FormLabel>
                                            <FormControl>
                                                <PasswordInput
                                                    type={!showNewPassword ? 'password' : 'text'}
                                                    placeholder='新しいパスワード'
                                                    autoComplete='off'
                                                    onToggleIcon={() => setShowNewPassword(!showNewPassword)}
                                                    {...field}
                                                    className='h-14 rounded-none'
                                                    onPaste={e => e.preventDefault()}
                                                    onChange={(value) => {
                                                        field.onChange(value);
                                                    }}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name='confirmPassword'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className='text-base' required>新しいパスワード確認</FormLabel>
                                            <FormControl>
                                                <PasswordInput
                                                    type={!showConfirmPassword ? 'password' : 'text'}
                                                    placeholder='新しいパスワードの確認'
                                                    autoComplete='false'
                                                    className='h-14 rounded-none'
                                                    onPaste={e => e.preventDefault()}
                                                    onToggleIcon={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    {...field}
                                                    onChange={(value) => {
                                                        field.onChange(value);
                                                    }}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <div className='flex gap-4 pt-4 md:gap-8'>
                                <Button
                                    type='button'
                                    variant='secondary'
                                    onClick={() => hideModal('form-change-password')}
                                    className='flex-1 rounded-lg h-14'
                                >
                                    キャンセル
                                </Button>
                                <Button
                                    type='submit'
                                    className='flex-1 rounded-lg h-14'
                                >
                                    パスワード変更
                                </Button>
                            </div>
                        </form>
                    </Form>
                </div>
            </div>
        </div>
    );
});
